import type { GroupEntry, ProductEntry } from "@innoclouds/db";
import { useMainLibStore, useModulesLibStore, useTableStore } from "@innoclouds/shared";
import { useLocalStorage } from "@vueuse/core";
import i18next from "i18next";
import { defineStore } from "pinia";
import { type Ref, reactive, ref, toRaw } from "vue";
import { useRouter } from "vue-router";
import useAxios from "../../../utils/api/useAxiosToken";
import getOrgUnitId from "../../../utils/useOrgUnitId";
import type { IProduct } from "../../../views/cardProductPage/iCardProduct";

export const useMainStore = defineStore("main", () => {
	const { axiosInstanceGet } = useAxios();

	const router = useRouter();

	const tableStore = useTableStore();
	const modulesLibStore = useModulesLibStore();
	const mainLibStore = useMainLibStore();
	const appMode: Ref<string | null> = useLocalStorage<string>("appMode", () => null);

	const cartProducts = ref<IProduct[]>([]);
	const countBonus = ref(0);

	const idActiveGroup = ref<GroupEntry["id"] | null>(null);
	const is_toggleAffiliation = ref(false);

	const currentActiveTag = reactive(new Map<string, string[]>());
	const currentActiveFolder = reactive(new Map<string, any[]>());
	const scrollCache = reactive<{ offset: number; cache: any; activeIndex: number }>({ offset: 0, cache: null, activeIndex: 0 });

	const typeClearUser = ref({
		addressId: "",
		type: "",
	});

	// Загрузка данных
	const loadCart = () => {
		try {
			const savedCart = localStorage.getItem("cart");
			if (savedCart) {
				cartProducts.value = JSON.parse(savedCart);
			}
			const savedBonus = localStorage.getItem("bonus");
			if (savedBonus) {
				countBonus.value = parseInt(savedBonus, 10);
			}
		} catch (error) {
			console.error("Failed to load cart from localStorage:", error);
		}
	};

	const setBonus = (value: number) => {
		countBonus.value = value;
		localStorage.setItem("bonus", value.toString());
	};

	// const totalPrice = computed(() => {
	//     if(cartProducts.value.length === 0) return 0

	//     const cartProductsCopy = cartProducts.value;
	//     return cartProductsCopy.reduce((sum, item) => {
	//         const price = PricePrerender(item) * item.amount;
	//         let modifiersGroupPrice = 0;
	//         let modifiersPrice = 0

	//         if(item.group_modifiers) {
	//             modifiersGroupPrice = item.group_modifiers.reduce((total: number, group: any) => {
	//                 if(group.modifiers) {
	//                     return total + group.modifiers.reduce((groupTotal: number, modifier: any) => {
	//                         return groupTotal + modifier.price * modifier.amount;
	//                     }, 0);
	//                 } else {
	//                     return 0
	//                 }
	//             }, 0) * item.amount;
	//         }
	//         if(item.modifiers) {
	//             modifiersPrice = item.modifiers.reduce((total: number, modifier: any) => {
	//                 return total + modifier.price * modifier.amount;
	//             }, 0) * item.amount
	//         }
	//         return sum + price + modifiersGroupPrice + modifiersPrice;
	//     }, 0);
	// });

	const addToCart = (item: any) => {
		if (cartProducts.value.some((elem) => item.id === elem.id)) {
			console.warn("Товар уже в корзине");
			return;
		}

		cartProducts.value.push(item);
		updateCartInLocalStorage();
	};

	const plusAmount = (id: ProductEntry["id"]) => {
		cartProducts.value = cartProducts.value.map((item) => (item.id === id ? { ...item, amount: item.amount + 1 } : item));
	};

	const minesAmount = (id: ProductEntry["id"]) => {
		cartProducts.value = cartProducts.value.map((item) => (item.id === id ? { ...item, amount: item.amount - 1 } : item));
	};

	// Удаление товара из корзины
	const deleteItemToCart = (id: ProductEntry["id"]) => {
		const index = cartProducts.value.findIndex((item) => item.id === id);
		if (index === -1) return;

		cartProducts.value.splice(index, 1);
		updateCartInLocalStorage();
	};

	// Обновление localStorage
	const updateCartInLocalStorage = (): void => {
		try {
			const plainCart = toRaw(cartProducts.value);

			if (!plainCart || plainCart.length === 0) {
				localStorage.removeItem("cart");
				return;
			}

			localStorage.setItem("cart", JSON.stringify(plainCart));
		} catch (error) {
			console.error("Failed to update cart in localStorage:", error);
		}
	};

	const activeGroup = (id: GroupEntry["id"]) => {
		idActiveGroup.value = id;
	};

	let isTableLoading = false;
	const takeDataTable = async (addressId: string, tableId: string | null, type: string = "link") => {
		if (!tableId || !addressId || isTableLoading) return;

		const uuidv4regex = /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;
		if (!uuidv4regex.test(tableId)) {
			modulesLibStore.is_notification_lib = true;
			// @tolgee-key { key: 'notification.incorrect-table', ns: 'translation' }
			modulesLibStore.notificationData_lib.textNotification = i18next.t(`notification.incorrect-table`);
			return;
		}

		if (localStorage.getItem("orgID") !== addressId && type === "qr") {
			const data: any = await getOrgUnitId(addressId);
			typeClearUser.value.addressId = addressId;

			if (data.organization !== mainLibStore.config_lib?.orgId) {
				// @tolgee-key { key: 'notification.qr-code-belongs-to-another-organization', ns: 'translation' }
				modulesLibStore.notificationData_lib.textNotification = i18next.t(`notification.qr-code-belongs-to-another-organization`);
				typeClearUser.value.type = "org";
			} else {
				// @tolgee-key { key: 'notification.qr-code-belongs-to-another-address', ns: 'translation' }
				modulesLibStore.notificationData_lib.textNotification = i18next.t(`notification.qr-code-belongs-to-another-address`);
				typeClearUser.value.type = "point";
			}
			modulesLibStore.notificationData_lib.buttonAccept = {
				// @tolgee-key { key: 'button.continue', ns: 'translation' }
				text: i18next.t(`button.continue`),
				state: true,
				disabled: false,
				acceptBtn: () => clearDataUser(),
			};
			modulesLibStore.is_notification_lib = true;
		}

		isTableLoading = true;
		axiosInstanceGet
			.get(`org_unit/${addressId}/table/${tableId}`)
			.then((response: any) => {
				tableStore.id = response.data.id;
			})
			.catch(() => {
				modulesLibStore.is_notification_lib = true;
				// @tolgee-key { key: 'camera-scanner.notification.table-not-found', ns: 'translation' }
				modulesLibStore.notificationData_lib.textNotification = i18next.t(`camera-scanner.notification.table-not-found`);
				tableStore.id = undefined;
			})
			.finally(() => {
				isTableLoading = false;
			});
	};

	const clearDataUser = async () => {
		router.push({ name: "home", params: { orgId: typeClearUser.value.addressId } });
		modulesLibStore.is_notification_lib = false;
	};

	loadCart();

	i18next.on("languageChanged", () => {
		currentActiveTag.clear();
	});

	return {
		cartProducts,
		count: countBonus,
		// totalPrice,
		idActiveGroup,
		is_toggleAffiliation,
		typeClearUser,
		currentActiveTag,
		appMode,
		scrollCache,
		currentActiveFolder,
		addToCart,
		deleteItemToCart,
		loadCart,
		activeGroup,
		plusAmount,
		minesAmount,
		takeDataTable,
		setBonus,
		updateCartInLocalStorage,
	};
});
